import Box from "@material-ui/core/Box";
import MuiButton from "@material-ui/core/Button";
import { SimpleFormAdv } from "components/forms/SimpleFormAdv";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import React from "react";
import { Tab, useRecordContext, useTranslate } from "react-admin";
import { CloneButton, DeleteButton } from "react-admin";
import { Link } from "react-router-dom";
import { isStorageModuleEnabled } from "utils/storageModule";

import { orderWarningValidations } from "../../../components/_helpers/warningsConditions";
import { ChangeCustomerButton } from "../../../components/dialogs/ChangeCustomerDialog";
import { DocumentsFieldBlock } from "../../../components/fields/DocumentsField";
import { SaveForceTruckArrivedForOrder } from "../../../components/toolbars/SaveForceTruckArrivedForOrder";
import { OSFCUserRoles, OrderService, OrderStatus, UserRole } from "../../../entities";
import { checkCanCloneOrder, checkCustomerCanDeleteOrder } from "../../../utils/checker/order";
import { EditFormWrapper, SaveWithTwoOptionsToolbar } from "../Edit/Edit";
import { ArrivedPopupProvider } from "../forms/ArrivedPopupContextManager";
import { FormCustomer } from "../forms/FormCustomer";
import { FormCustomerTransporter } from "../forms/FormCustomerTransporter";
import { FormManager } from "../forms/FormManager";
import { FormTransporter } from "../forms/FormTransporter";

const unloadingDisable = (record) => !record.eta_date;
const loadingDisable = (record) => !record.etd_date;

const getCloneRecord = (record) => {
	const cloned = {
		...record,
		reference: null,
		origin_id: record.id,
		status: OrderStatus.BOOKED,
	};

	if (
		record.service === OrderService.coldStorage ||
		(record.service === OrderService.intoPlukkStorage && record.etd_date)
	) {
		cloned.pallets = null;
		cloned.boxes = null;
		cloned.kilos = null;
	}

	return cloned;
};

const OutDocumentsButton = ({ basePath, record, translate }) => {
	const unloading_disable = unloadingDisable(record);
	const loading_disable = loadingDisable(record);
	if (unloading_disable && loading_disable) return null;
	return (
		<Box mr={2}>
			<MuiButton
				component={Link}
				to={`${basePath}/${record.id}/show/internal-documents`}
				size="small"
				color="primary"
				variant="outlined"
			>
				{translate("tab.out-documents.name")}
			</MuiButton>
		</Box>
	);
};

export const EditTab = ({ permissions, ...props }) => {
	const record = useRecordContext();
	const translate = useTranslate();
	const { data: terminals } = useTerminalsContext();
	const usesStorageModule = isStorageModuleEnabled(terminals[record?.terminal_id]);

	// choose form component by role
	let FormComp = FormManager;
	const role = permissions?.role;
	if (role === UserRole.customer_manager || role === UserRole.customer_employee) {
		FormComp = FormCustomer;
	} else if (
		role === UserRole.customer_and_transporter_manager ||
		role === UserRole.customer_and_transporter_employee ||
		role === UserRole.customer_and_transporter_driver
	) {
		FormComp = FormCustomerTransporter;
	} else if (
		role === UserRole.transporter_manager ||
		role === UserRole.transporter_employee ||
		role === UserRole.transporter_driver
	) {
		FormComp = FormTransporter;
	}

	const toolbarCompProps = {
		warningValidations: orderWarningValidations,
		...(permissions &&
			OSFCUserRoles.has(permissions.role) && { saveComp: SaveForceTruckArrivedForOrder }),
	};

	// Determine which action buttons should be visible
	const isDraft = record?.status === OrderStatus.DRAFT;
	let hasClone = false;
	let hasChange = false;
	let hasExtraButtons = false;
	let hasDelete = false;

	if (permissions) {
		if (
			permissions.role === UserRole.osfc_manager ||
			permissions.role === UserRole.app_manager ||
			permissions.role === UserRole.admin
		) {
			hasClone = checkCanCloneOrder(record);
			hasChange = !isDraft;
			hasExtraButtons = !isDraft;
			hasDelete = isDraft;
		} else if (
			permissions.role === UserRole.customer_manager ||
			permissions.role === UserRole.customer_employee
		) {
			hasDelete = checkCustomerCanDeleteOrder(record);
			hasClone = checkCanCloneOrder(record);
			hasExtraButtons = !isDraft;
		} else if (
			permissions.role === UserRole.customer_and_transporter_manager ||
			permissions.role === UserRole.customer_and_transporter_employee ||
			permissions.role === UserRole.customer_and_transporter_driver
		) {
			hasExtraButtons = !isDraft;
			if (permissions.company_id && permissions.company_id === record?.company_id) {
				hasClone = checkCanCloneOrder(record);
				hasDelete = checkCustomerCanDeleteOrder(record);
			}
		} else {
			hasExtraButtons = !isDraft;
			hasClone = checkCanCloneOrder(record);
		}
	}

	return (
		<Tab label="Edit" {...props}>
			<Box display="flex" mb={2} gap={2} ml={2} alignItems="center">
				{hasClone && (
					<Box mr={2}>
						<CloneButton
							variant="outlined"
							basePath={props.basePath}
							record={getCloneRecord(record)}
						/>
					</Box>
				)}
				{hasDelete && (
					<Box mr={2}>
						<DeleteButton undoable={false} basePath={props.basePath} record={record} />
					</Box>
				)}
				{hasChange && (
					<Box mr={2}>
						<ChangeCustomerButton userId={permissions?.id} record={record} />
					</Box>
				)}

				{!usesStorageModule && (
					<OutDocumentsButton basePath={props.basePath} record={record} translate={translate} />
				)}
			</Box>
			<ArrivedPopupProvider>
				<EditFormWrapper resource={props.resource} basePath={props.basePath} record={record}>
					<SimpleFormAdv
						toolbarComp={SaveWithTwoOptionsToolbar}
						toolbarCompProps={toolbarCompProps}
						record={record}
					>
						<FormComp isAddMode={false} record={record} permissions={permissions} />
					</SimpleFormAdv>
				</EditFormWrapper>
				<DocumentsFieldBlock {...props} variant="elevation" showDownloadAll={true} />
			</ArrivedPopupProvider>
		</Tab>
	);
};

export default EditTab;
